import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { NATUREZA_LABELS, type NaturezaDoCusto, type TipoFinanceiro } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { cn } from "@/lib/cn";
import type { CategoriaFinanceira } from "@/lib/dinheiro";
import { campo, moldura } from "../estilos";

type Rascunho = { id?: string; nome: string; tipo: TipoFinanceiro; natureza: NaturezaDoCusto; cor: string };

function Editor({ inicial, onFechar }: { inicial: Rascunho; onFechar: () => void }) {
  const utils = trpc.useUtils();
  const [r, setR] = useState(inicial);
  const salvar = trpc.financeiro.categorias.salvar.useMutation({
    onSuccess: () => {
      toast.success("Categoria salva.");
      utils.financeiro.invalidate();
      onFechar();
    },
    onError: (e) => toast.error(e.message),
  });
  return (
    <div className="flex flex-wrap items-center gap-2 bg-zinc-50/60 px-4 py-3 dark:bg-muted/30">
      <input type="color" value={r.cor} onChange={(e) => setR({ ...r, cor: e.target.value })} aria-label="Cor" className="h-9 w-10 cursor-pointer rounded-md border border-zinc-200 bg-white p-1 dark:border-border dark:bg-card" />
      <input autoFocus value={r.nome} onChange={(e) => setR({ ...r, nome: e.target.value })} placeholder="Nome da categoria" className={cn(campo, "min-w-[180px] flex-1")} />
      {r.tipo === "SAIDA" && (
        <select value={r.natureza} onChange={(e) => setR({ ...r, natureza: e.target.value as NaturezaDoCusto })} className={cn(campo, "w-36")}>
          <option value="FIXO">Custo fixo</option>
          <option value="VARIAVEL">Custo variável</option>
        </select>
      )}
      <button type="button" onClick={onFechar} className="h-9 rounded-md px-3 text-sm text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted">
        Cancelar
      </button>
      <button
        type="button"
        disabled={r.nome.trim().length < 2 || salvar.isPending}
        onClick={() => salvar.mutate(r)}
        className="h-9 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
      >
        Salvar
      </button>
    </div>
  );
}

function Grupo({ tipo, categorias, pode }: { tipo: TipoFinanceiro; categorias: CategoriaFinanceira[]; pode: boolean }) {
  const utils = trpc.useUtils();
  const [editando, setEditando] = useState<Rascunho | null>(null);
  const alternar = trpc.financeiro.categorias.alternarAtiva.useMutation({ onSuccess: () => utils.financeiro.invalidate() });
  const novo: Rascunho = { nome: "", tipo, natureza: tipo === "SAIDA" ? "FIXO" : "VARIAVEL", cor: "#71717a" };

  return (
    <section className={cn(moldura, "overflow-hidden")}>
      <header className="flex items-center justify-between border-b border-zinc-100 bg-zinc-50/60 px-4 py-2.5 dark:border-border dark:bg-muted/30">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">{tipo === "ENTRADA" ? "Categorias de entrada" : "Categorias de saída"}</h3>
        {pode && (
          <button type="button" onClick={() => setEditando(novo)} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline dark:text-primary">
            <Plus className="h-3.5 w-3.5" /> Nova categoria
          </button>
        )}
      </header>
      {editando && !editando.id && <Editor inicial={editando} onFechar={() => setEditando(null)} />}
      <ul className="divide-y divide-zinc-100 dark:divide-border/50">
        {categorias.map((c) =>
          editando?.id === c.id ? (
            <li key={c.id}>
              <Editor inicial={editando} onFechar={() => setEditando(null)} />
            </li>
          ) : (
            <li key={c.id} className={cn("flex items-center gap-3 px-4 py-2.5", !c.ativa && "opacity-50")}>
              <span className="h-3 w-3 shrink-0 rounded-[3px]" style={{ backgroundColor: c.cor || "#A1A1AA" }} />
              <span className="flex-1 text-sm text-zinc-900 dark:text-foreground">{c.nome}</span>
              {tipo === "SAIDA" && (
                <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10.5px] font-semibold text-zinc-600 dark:bg-muted dark:text-muted-foreground">
                  {NATUREZA_LABELS[c.natureza]}
                </span>
              )}
              {pode && (
                <>
                  <button type="button" aria-label={`Editar ${c.nome}`} onClick={() => setEditando({ id: c.id, nome: c.nome, tipo, natureza: c.natureza, cor: c.cor ?? "#71717a" })} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-muted">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => alternar.mutate({ id: c.id, ativa: !c.ativa })} className="w-20 text-right text-xs font-semibold text-zinc-500 hover:text-zinc-900 dark:hover:text-foreground">
                    {c.ativa ? "Desativar" : "Reativar"}
                  </button>
                </>
              )}
            </li>
          ),
        )}
      </ul>
    </section>
  );
}

/** Categorias não se apagam (lançamentos apontam para elas): desativar tira das escolhas. */
export function CategoriasFinanceiras() {
  const pode = usePermissao("finance.manage");
  const categorias = trpc.financeiro.categorias.listar.useQuery().data ?? [];
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Grupo tipo="SAIDA" categorias={categorias.filter((c) => c.tipo === "SAIDA")} pode={pode} />
      <Grupo tipo="ENTRADA" categorias={categorias.filter((c) => c.tipo === "ENTRADA")} pode={pode} />
    </div>
  );
}
